/**
 * Commandes envoyées par un client. Le serveur ne fait jamais confiance au
 * client : `parseCommand` valide la forme (types, bornes) avant toute
 * validation métier côté simulation.
 */
import { BUILDINGS, ECONOMY, LIFTS, type BuildingKind, type LiftKind, type SupplyKind } from './catalog';
import type { Vec2 } from './geometry';
import type { Difficulty, EntityId, PisteRoute, PisteSize } from './state';
import { SPEEDS, type SimSpeed } from './time';

/** Points de passage d'une piste tracée (départ et arrivée compris). */
export const MAX_PISTE_POINTS = 24;

export type Command =
  | { type: 'build'; kind: BuildingKind; x: number; z: number; rot: number }
  | { type: 'buildLift'; kind: LiftKind; bottom: Vec2; top: Vec2; difficulty: Difficulty | 'auto' }
  | { type: 'buildPiste'; points: Vec2[]; route: PisteRoute; size: PisteSize }
  | { type: 'editPiste'; id: EntityId; points: Vec2[]; route: PisteRoute; size: PisteSize }
  | { type: 'renamePiste'; id: EntityId; name: string }
  | { type: 'demolish'; id: EntityId }
  | { type: 'setLiftOpen'; id: EntityId; open: boolean }
  | { type: 'repairLift'; id: EntityId }
  | { type: 'setPisteOpen'; id: EntityId; open: boolean }
  | { type: 'setSnowmaking'; id: EntityId; on: boolean }
  | { type: 'setPisteGrooming'; id: EntityId; on: boolean }
  | { type: 'setTicketPrice'; price: number }
  | { type: 'setMaintenance'; level: 0 | 1 | 2 }
  | { type: 'orderSupply'; supply: SupplyKind; qty: number }
  | { type: 'setSpeed'; speed: SimSpeed }
  | { type: 'dismissAlert'; id: EntityId }
  | { type: 'renameStation'; name: string };

export type CommandType = Command['type'];

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const isNum = (v: unknown, lo = -1e7, hi = 1e7): v is number => typeof v === 'number' && Number.isFinite(v) && v >= lo && v <= hi;
const isId = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0 && v < 2 ** 31;
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isVec2 = (v: unknown): v is Vec2 => Array.isArray(v) && v.length === 2 && isNum(v[0], -5000, 5000) && isNum(v[1], -5000, 5000);
const DIFFS = ['verte', 'bleue', 'rouge', 'noire', 'auto'];

/** Nom de station : lettres, chiffres, espaces et ponctuation simple, 3 à 32 caractères. */
export function isValidStationName(name: unknown): name is string {
  return typeof name === 'string' && /^[\p{L}\p{N}][\p{L}\p{N} '\-.]{2,31}$/u.test(name);
}

/** Nom de piste : mêmes caractères, 2 à 24. */
export function isValidPisteName(name: unknown): name is string {
  return typeof name === 'string' && /^[\p{L}\p{N}][\p{L}\p{N} '\-.]{1,23}$/u.test(name.trim());
}

const ROUTES = ['direct', 'verte', 'bleue', 'rouge', 'noire'];

function parsePoints(v: unknown): Vec2[] | null {
  if (!Array.isArray(v) || v.length < 2 || v.length > MAX_PISTE_POINTS || !v.every(isVec2)) return null;
  return (v as Vec2[]).map((p) => [Math.round(p[0] * 10) / 10, Math.round(p[1] * 10) / 10]);
}

/** Valide la forme d'une commande. Renvoie null si elle est mal formée. */
export function parseCommand(raw: unknown): Command | null {
  if (!isObj(raw) || typeof raw.type !== 'string') return null;
  switch (raw.type) {
    case 'build':
      if (typeof raw.kind === 'string' && Object.hasOwn(BUILDINGS, raw.kind) && isNum(raw.x, -5000, 5000) && isNum(raw.z, -5000, 5000) && isNum(raw.rot, -10, 10)) {
        return { type: 'build', kind: raw.kind as BuildingKind, x: raw.x, z: raw.z, rot: raw.rot };
      }
      return null;
    case 'buildLift':
      if (typeof raw.kind === 'string' && Object.hasOwn(LIFTS, raw.kind) && isVec2(raw.bottom) && isVec2(raw.top)) {
        const difficulty = typeof raw.difficulty === 'string' && DIFFS.includes(raw.difficulty) ? (raw.difficulty as Difficulty | 'auto') : 'auto';
        return { type: 'buildLift', kind: raw.kind as LiftKind, bottom: [raw.bottom[0], raw.bottom[1]], top: [raw.top[0], raw.top[1]], difficulty };
      }
      return null;
    case 'buildPiste':
    case 'editPiste': {
      const points = parsePoints(raw.points);
      if (!points || (raw.type === 'editPiste' && !isId(raw.id))) return null;
      const route = typeof raw.route === 'string' && ROUTES.includes(raw.route) ? (raw.route as PisteRoute) : 'direct';
      const size: PisteSize = raw.size === 'narrow' || raw.size === 'wide' ? raw.size : 'normal';
      return raw.type === 'buildPiste' ? { type: 'buildPiste', points, route, size } : { type: 'editPiste', id: raw.id as number, points, route, size };
    }
    case 'renamePiste':
      return isId(raw.id) && isValidPisteName(raw.name) ? { type: 'renamePiste', id: raw.id, name: raw.name.trim() } : null;
    case 'demolish':
    case 'repairLift':
    case 'dismissAlert':
      return isId(raw.id) ? ({ type: raw.type, id: raw.id } as Command) : null;
    case 'setLiftOpen':
    case 'setPisteOpen':
      return isId(raw.id) && isBool(raw.open) ? ({ type: raw.type, id: raw.id, open: raw.open } as Command) : null;
    case 'setSnowmaking':
      return isId(raw.id) && isBool(raw.on) ? { type: 'setSnowmaking', id: raw.id, on: raw.on } : null;
    case 'setPisteGrooming':
      return isId(raw.id) && isBool(raw.on) ? { type: 'setPisteGrooming', id: raw.id, on: raw.on } : null;
    case 'setTicketPrice':
      return isNum(raw.price, ECONOMY.minTicketPrice, ECONOMY.maxTicketPrice) ? { type: 'setTicketPrice', price: Math.round(raw.price) } : null;
    case 'setMaintenance':
      return raw.level === 0 || raw.level === 1 || raw.level === 2 ? { type: 'setMaintenance', level: raw.level } : null;
    case 'orderSupply':
      return (raw.supply === 'food' || raw.supply === 'fuel') && isNum(raw.qty, 1, 100_000)
        ? { type: 'orderSupply', supply: raw.supply, qty: Math.round(raw.qty) }
        : null;
    case 'setSpeed':
      return SPEEDS.includes(raw.speed as SimSpeed) ? { type: 'setSpeed', speed: raw.speed as SimSpeed } : null;
    case 'renameStation':
      return isValidStationName(raw.name) ? { type: 'renameStation', name: raw.name.trim() } : null;
    default:
      return null;
  }
}

/** Commandes qui modifient le monde partagé (réservées à l'hôte). */
export const WORLD_COMMANDS: ReadonlySet<CommandType> = new Set(['setSpeed']);
